import type { EntityInstance, MapDocument, ValidationIssue } from "daedalus-core";
import { FIELD } from "../editor/type-names.ts";
import { carvePatches, cropCellsOf, tilesPerCropCell, type CropCell } from "./field-cells.ts";
import { readFieldFields, type FieldFields, type FieldKind } from "./field-marker.ts";

/**
 * The `field` markers of one map (ADR-144 decision 6): each one read, laid on the map's crop cells and checked, once,
 * for both the editor's Issues list and the compile, so the two never disagree.
 */

/**
 * A field as the compile keeps it: where it is, what it is, whose it is, its crop cells as `[column, row, length]`
 * runs along a row, and for a rental its patch size and the top-left cell of each patch.
 */
export interface CompiledField {
  readonly map: string;
  readonly id: string;
  readonly kind: FieldKind;
  readonly npc: string;
  readonly patchSize?: number;
  readonly runs: readonly (readonly [number, number, number])[];
  readonly patches?: readonly (readonly [number, number])[];
}

/**
 * What a map's fields come to: the ones that are whole, and an issue for each thing wrong with the rest.
 */
export interface MapFields {
  readonly fields: readonly CompiledField[];
  readonly issues: readonly ValidationIssue[];
}

/**
 * Whether a map lies indoors: it says so, or it is a room off the grid and says nothing (ADR-122 decision 6).
 *
 * @param map The map.
 * @returns True when indoors.
 */
function isIndoor(map: MapDocument): boolean {
  const light = map.atmosphere?.light;
  return light === "indoor" || (light === undefined && map.host !== undefined);
}

/**
 * A field's cells as runs along a row.
 *
 * @param cells The cells in reading order.
 * @returns Each run's column, row and length.
 */
function runsOf(cells: readonly CropCell[]): [number, number, number][] {
  const runs: [number, number, number][] = [];
  for (const cell of cells) {
    const last = runs[runs.length - 1];
    if (last !== undefined && last[1] === cell.row && last[0] + last[2] === cell.col) {
      last[2] += 1;
    } else {
      runs.push([cell.col, cell.row, 1]);
    }
  }
  return runs;
}

/**
 * The fields a map's markers make.
 *
 * @param map The map.
 * @param tileSize The world's tile size in pixels; without it only each marker's own fields are checked.
 * @returns The whole fields in marker order, and the issues of the rest.
 */
export function readMapFields(map: MapDocument, tileSize: number | undefined): MapFields {
  const fields: CompiledField[] = [];
  const issues: ValidationIssue[] = [];
  const owners = new Map<string, string>();
  const per = tileSize === undefined ? null : tilesPerCropCell(tileSize);
  let tileSizeTold = false;
  for (const layer of map.layers) {
    if (layer.kind !== "entity") {
      continue;
    }
    for (const entity of layer.entities) {
      if (entity.type !== FIELD) {
        continue;
      }
      /**
       * Records one issue against this marker.
       * @param kind The issue's kind.
       * @param message The sentence after the marker's name.
       * @param severity `warning` for an issue the compile accepts.
       */
      const issue = (kind: string, message: string, severity?: "warning"): void => {
        issues.push({ mapId: map.id, kind, layerId: layer.id, entityId: entity.id, message: `field ${entity.id} ${message}`, ...(severity === undefined ? {} : { severity, ignorable: true as const }) });
      };
      const verdict = readFieldFields(entity.fields);
      if (!verdict.ok) {
        verdict.errors.forEach((error) => issue("field.field", error));
        continue;
      }
      if (tileSize === undefined) {
        continue;
      }
      if (per === null) {
        if (!tileSizeTold) {
          tileSizeTold = true;
          issues.push({ mapId: map.id, kind: "field.tile-size", message: `fields need a tile size that divides 32 px, not ${tileSize}; change the world's tile size or remove the fields` });
        }
        continue;
      }
      const compiled = compileOne(map, entity, verdict.field, tileSize, per, owners, issue);
      if (compiled !== null) {
        fields.push(compiled);
      }
    }
  }
  return { fields, issues };
}

/**
 * Lays one valid field on the map and checks it.
 *
 * @param map The map.
 * @param entity The marker.
 * @param field Its fields.
 * @param tileSize The world's tile size.
 * @param per The tiles a crop cell is wide and tall.
 * @param owners Which field holds each crop cell so far, filled as fields are laid.
 * @param issue Records an issue against the marker.
 * @returns The compiled field, or null when it raised an error; a warning leaves it compiled.
 */
function compileOne(map: MapDocument, entity: EntityInstance, field: FieldFields, tileSize: number, per: number, owners: Map<string, string>, issue: (kind: string, message: string, severity?: "warning") => void): CompiledField | null {
  if (isIndoor(map)) {
    issue("field.indoor", "is on an indoor map; fields lie outdoors, so move it to an outdoor map");
    return null;
  }
  const cells = cropCellsOf(entity, field.cells, tileSize);
  const columns = Math.ceil(map.width / per);
  const rows = Math.ceil(map.height / per);
  if (cells.some((cell) => cell.col < 0 || cell.row < 0 || cell.col >= columns || cell.row >= rows)) {
    issue("field.off-map", "reaches off the map; erase the cells past its edge");
    return null;
  }
  const rivals = new Set<string>();
  for (const cell of cells) {
    const rival = owners.get(`${cell.col},${cell.row}`);
    if (rival !== undefined) {
      rivals.add(rival);
    }
  }
  if (rivals.size > 0) {
    issue("field.overlap", `shares crop cells with field ${[...rivals].join(", ")}; repaint one so no crop cell is in both`);
    return null;
  }
  const base = { map: map.id, id: entity.id, kind: field.kind, npc: field.farmNpc, runs: runsOf(cells) };
  if (field.kind === "shared") {
    claim(owners, cells, entity.id);
    return base;
  }
  const { patches, leftover } = carvePatches(cells, field.patchSize);
  if (patches.length === 0) {
    issue("field.no-patch", `holds no whole patch; paint at least a ${field.patchSize} by ${field.patchSize} block of cells`);
    return null;
  }
  if (leftover > 0) {
    issue("field.leftover", `has ${leftover} painted ${leftover === 1 ? "cell that won't be a plot" : "cells that won't be plots"}; fill ${leftover === 1 ? "it" : "them"} to whole ${field.patchSize} by ${field.patchSize} patches, or erase ${leftover === 1 ? "it" : "them"}`, "warning");
  }
  claim(owners, cells, entity.id);
  return { ...base, runs: runsOf(patchCells(patches, field.patchSize)), patchSize: field.patchSize, patches: patches.map((patch): [number, number] => [patch.col, patch.row]) };
}

/**
 * Records a field as the owner of its crop cells, so a later field that shares one is refused. Only a field that compiled
 * claims, so a field in error never blocks another.
 *
 * @param owners Which field holds each crop cell so far.
 * @param cells The field's crop cells.
 * @param id The field's marker id.
 */
function claim(owners: Map<string, string>, cells: readonly CropCell[], id: string): void {
  for (const cell of cells) {
    owners.set(`${cell.col},${cell.row}`, id);
  }
}

/**
 * Every crop cell the patches cover, in reading order.
 *
 * @param patches The patches by top-left cell.
 * @param patchSize The cells a patch is wide and tall.
 * @returns The cells.
 */
function patchCells(patches: readonly CropCell[], patchSize: number): CropCell[] {
  const cells: CropCell[] = [];
  for (const patch of patches) {
    for (let dy = 0; dy < patchSize; dy += 1) {
      for (let dx = 0; dx < patchSize; dx += 1) {
        cells.push({ col: patch.col + dx, row: patch.row + dy });
      }
    }
  }
  return cells.sort((a, b) => a.row - b.row || a.col - b.col);
}

/**
 * The Issues a map's fields raise in the editor and at a compile, each naming its marker.
 *
 * @param map The map.
 * @param tileSize The world's tile size in pixels, undefined where the caller has none.
 * @returns The issues.
 */
export function fieldIssues(map: MapDocument, tileSize: number | undefined): ValidationIssue[] {
  return [...readMapFields(map, tileSize).issues];
}
